import React, { useEffect, useState } from "react";
import {
  Layout,
  Card,
  Tag,
  Button,
  Table,
  Image,
  Typography,
  Spin,
  Modal,
  Descriptions,
  Popconfirm,
  message,
} from "antd";
import { EyeOutlined, MenuOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../Config";
import Sidebar from "../Components/Sidebar";

const { Content } = Layout;
const { Text, Title } = Typography;

const MyAsanas = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    fetchAsanas();
  }, []);

  const fetchAsanas = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await axios.get(`${BASE_URL}/userasanas/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data.asanas || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = sessionStorage.getItem("token");
      await axios.delete(`${BASE_URL}/userasanas/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Asana deleted");
      fetchAsanas();
    } catch (err) {
      message.error(err.response?.data?.message || "Delete failed");
    }
  };

  const statusColor = (s) =>
    s === "pending" ? "orange" : s === "approved" ? "green" : "red";

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Level",
      dataIndex: "level",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <Tag color={statusColor(s)}>{s?.toUpperCase()}</Tag>,
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      render: (d) => new Date(d).toLocaleDateString(),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              setViewData(record);
              setViewOpen(true);
            }}
          />
          {record.status !== "approved" && (
            <Popconfirm
              title="Delete Asana"
              description="Are you sure you want to delete this asana?"
              onConfirm={() => handleDelete(record._id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button icon={<DeleteOutlined />} danger />
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const MobileCard = ({ item }) => (
    <Card
      style={{
        marginBottom: 12,
        borderRadius: 16,
        border: "1px solid #f0f0f0",
      }}
      bodyStyle={{ padding: 14 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <Text strong style={{ fontSize: 15 }}>
            {item.name}
          </Text>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            {item.level}
          </div>
        </div>
        <Tag color={statusColor(item.status)}>{item.status?.toUpperCase()}</Tag>
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: "#aaa" }}>
        Submitted: {new Date(item.createdAt).toLocaleDateString()}
      </div>
      <div
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <Button
          icon={<EyeOutlined />}
          size="small"
          onClick={() => {
            setViewData(item);
            setViewOpen(true);
          }}
        >
          View
        </Button>
        {item.status !== "approved" && (
          <Popconfirm
            title="Delete this asana?"
            onConfirm={() => handleDelete(item._id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        )}
      </div>
    </Card>
  );

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        collapsedWidth="0"
        role="user"
      />

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 0 : 240,
          transition: "margin 0.3s",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 24px",
            background: "#fff",
            borderBottom: "1px solid #f0f0f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}
        >
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: 20 }} />}
              onClick={() => setMobileOpen(true)}
              style={{
                background: "#f5f3ff",
                color: "#7c3aed",
                border: "none",
                borderRadius: 8,
              }}
            />
          )}
          <Title level={4} style={{ margin: 0, color: "#1e1b4b" }}>
            My Asanas
          </Title>
        </div>

        <Content style={{ padding: isMobile ? 12 : 24 }}>
          <Card
            style={{ borderRadius: 16 }}
            bodyStyle={{ padding: isMobile ? 12 : 24 }}
          >
            {loading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin size="large" />
              </div>
            ) : data.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
                No asanas submitted yet
              </div>
            ) : isMobile ? (
              data.map((item) => <MobileCard key={item._id} item={item} />)
            ) : (
              <Table
                columns={columns}
                dataSource={data}
                rowKey="_id"
                loading={loading}
              />
            )}
          </Card>
        </Content>
      </Layout>

      {/* View Modal */}
      <Modal
        title="Asana Details"
        open={viewOpen}
        onCancel={() => setViewOpen(false)}
        footer={<Button onClick={() => setViewOpen(false)}>Close</Button>}
        width={isMobile ? "95vw" : 600}
        centered
      >
        {viewData && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Name">{viewData.name}</Descriptions.Item>
            <Descriptions.Item label="Sanskrit">
              {viewData.sanskrit || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Tamil">
              {viewData.tamil || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="English">
              {viewData.english || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Level">
              {viewData.level || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Pose Type">
              {viewData.poseType?.join(", ") || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColor(viewData.status)}>
                {viewData.status?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Submitted">
              {new Date(viewData.createdAt).toLocaleDateString()}
            </Descriptions.Item>
            {viewData.mainImage && (
              <Descriptions.Item label="Main Image">
                <Image
                  width={200}
                  src={viewData.mainImage}
                  style={{ borderRadius: 8 }}
                />
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </Layout>
  );
};

export default MyAsanas;
